import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import callApi from 'lib/callApi';
import { companyIdAtom } from 'views/state';

import { checkNonZeroArray } from '../../helper';
import { DashboardType, MetricsType, MetricsView } from '../../types';
import { SASTType } from '../config';
import { appliedRowIdsAtom, sastTypeAtom, severityFiltersAtom } from '../state';

export const isMetricsSectionOpenAtom = atom(false);
export const metricsViewAtom = atomWithStorage(
  'sast_metrics_view',
  MetricsView.Severity,
);
export const timeViewLabels = [
  '0 to 3 Days',
  '3+ to 7 Days',
  '7+ to 14 Days',
  '14+ to 28 Days',
  '28+ Days',
];

export const vulnMetricsDataAtom = atom(async (get) => {
  const isOpen = get(isMetricsSectionOpenAtom);
  if (isOpen) {
    const companyId = get(companyIdAtom);
    const appliedSASTIds = get(appliedRowIdsAtom);

    try {
      if (companyId && companyId !== -1) {
        const params = {
          company_id: companyId,
          host_type: DashboardType.SAST2,
        };
        if (appliedSASTIds?.length > 0) {
          params['obj_id_list'] = appliedSASTIds;
        }

        const responseData = await callApi('dashboard/metrics', 'GET', params);
        return responseData?.data;
      }
    } catch (error) {
      console.error(error);
    }
  }
  return undefined;
});

export const openVulnMetricsBySeverityAverageDayTotalVulnAtom = atom(
  async (get) => {
    const data: any = await get(vulnMetricsDataAtom);
    const sastType = get(sastTypeAtom);
    const severityFilters = get(severityFiltersAtom);
    if (data) {
      return getVulnMetrics(
        data.severityBasedData,
        sastType,
        MetricsType.Open,
        'Days',
        severityFilters,
      );
    }
    return undefined;
  },
);

export const openVulnMetricsBySeverityCountsAtom = atom(async (get) => {
  const data: any = await get(vulnMetricsDataAtom);
  const sastType = get(sastTypeAtom);
  const severityFilters = get(severityFiltersAtom);
  if (data) {
    return getVulnMetrics(
      data.severityBasedData,
      sastType,
      MetricsType.Open,
      'Count',
      severityFilters,
    );
  }
  return undefined;
});

export const closeVulnMetricsBySeverityAverageDayTotalVulnAtom = atom(
  async (get) => {
    const data: any = await get(vulnMetricsDataAtom);
    const sastType = get(sastTypeAtom);
    const severityFilters = get(severityFiltersAtom);
    if (data) {
      return getVulnMetrics(
        data.severityBasedData,
        sastType,
        MetricsType.Closed,
        'Days',
        severityFilters,
      );
    }
    return undefined;
  },
);

export const closedVulnMetricsBySeverityCountsAtom = atom(async (get) => {
  const data: any = await get(vulnMetricsDataAtom);
  const sastType = get(sastTypeAtom);
  const severityFilters = get(severityFiltersAtom);
  if (data) {
    return getVulnMetrics(
      data.severityBasedData,
      sastType,
      MetricsType.Closed,
      'Count',
      severityFilters,
    );
  }
  return undefined;
});

export const openVulnMetricsByTimeCountsAtom = atom(async (get) => {
  const data: any = await get(vulnMetricsDataAtom);
  const sastType = get(sastTypeAtom);
  const severityFilters = get(severityFiltersAtom);
  if (data) {
    return getVulnMetricsByTimeData(
      data.timeBasedData,
      sastType,
      MetricsType.Open,
      severityFilters,
    );
  }
  return undefined;
});

export const closedVulnMetricsByTimeCountsAtom = atom(async (get) => {
  const data: any = await get(vulnMetricsDataAtom);
  const sastType = get(sastTypeAtom);
  const severityFilters = get(severityFiltersAtom);
  if (data) {
    return getVulnMetricsByTimeData(
      data.timeBasedData,
      sastType,
      MetricsType.Closed,
      severityFilters,
    );
  }
  return undefined;
});

export const openVulnMetricsTooltipAtom = atom(async (get) => {
  const data: any = await get(vulnMetricsDataAtom);
  const sastType = get(sastTypeAtom);
  if (data) {
    return getVulnMetricsTooltips(
      data.severityBasedData,
      sastType,
      MetricsType.Open,
      'Days',
    );
  }
  return [];
});

export const closedVulnMetricsTooltipAtom = atom(async (get) => {
  const data: any = await get(vulnMetricsDataAtom);
  const sastType = get(sastTypeAtom);
  if (data) {
    return getVulnMetricsTooltips(
      data.severityBasedData,
      sastType,
      MetricsType.Closed,
      'Days',
    );
  }
  return [];
});

function getVulnMetrics(
  sourceData: any,
  sastType: SASTType,
  metricsType: MetricsType,
  propertyName: string,
  severityFilter: any,
) {
  const bugNode = sourceData[SASTType.Bug][metricsType];
  const vulnNode = sourceData[SASTType.Vulnerability][metricsType];

  const bugValues: any[] = Object.values(bugNode);
  const vulnValues: any[] = Object.values(vulnNode);

  const bugData = bugValues.map((x) => x[propertyName]);
  const vulnData = vulnValues.map((x) => x[propertyName]);

  const resultsArray: number[][] = [];
  if (
    (sastType === SASTType.Bug || sastType === SASTType.All) &&
    checkNonZeroArray(bugData)
  )
    resultsArray.push(bugData);
  if (
    (sastType === SASTType.Vulnerability || sastType === SASTType.All) &&
    checkNonZeroArray(vulnData)
  )
    resultsArray.push(vulnData);

  const averageDaysOpenClose = getVulnMetricsAverageDaysOpenClose(
    sourceData,
    sastType,
    metricsType,
    severityFilter,
  );
  const totalVulnsCount = getVulnMetricsTotalVulnsCount(
    sourceData,
    sastType,
    metricsType,
    severityFilter,
  );
  return {
    metricsData: resultsArray,
    averageDaysOpenClose: averageDaysOpenClose,
    totalVulnsCount: totalVulnsCount,
  };
}

function getVulnMetricsTotalVulnsCount(
  sourceData: any,
  sastType: SASTType,
  metricsType: MetricsType,
  severityFilters: string[],
) {
  const filteredMetricsBySeverity = filterMetricsBySeverity(
    sourceData,
    sastType,
    metricsType,
    severityFilters,
  );

  const validVulnerabilities = filteredMetricsBySeverity.filter(
    (v) => v.Count > 0,
  );
  const totalCount = validVulnerabilities.reduce((sum, v) => sum + v.Count, 0);
  return totalCount;
}

function getVulnMetricsAverageDaysOpenClose(
  sourceData: any,
  sastType: SASTType,
  metricsType: MetricsType,
  severityFilters: any,
) {
  let filteredMetricsBySeverity = filterMetricsBySeverity(
    sourceData,
    sastType,
    metricsType,
    severityFilters,
  );

  const validVulnerabilities = filteredMetricsBySeverity.filter(
    (v) => v.Count > 0,
  );
  const totalDays = validVulnerabilities.reduce(
    (sum, v) => sum + v.Days * v.Count,
    0,
  );
  const totalCount = validVulnerabilities.reduce((sum, v) => sum + v.Count, 0);
  const averageDaysOpenClose = totalCount > 0 ? totalDays / totalCount : 0;
  return averageDaysOpenClose.toFixed(1);
}

function filterMetricsBySeverity(
  sourceData: any,
  sastType: SASTType,
  metricsType: MetricsType,
  severityFilters: any,
) {
  const vulnNode = sourceData[SASTType.Vulnerability][metricsType];
  const bugNode = sourceData[SASTType.Bug][metricsType];

  const severities = ['Urgent', 'Critical', 'Medium', 'Minimal', 'Serious'];

  let resultsArray: any[] = [];
  severities.forEach((severity) => {
    if (severityFilters.includes(severity)) {
      if (sastType === SASTType.Bug || sastType === SASTType.All)
        resultsArray.push(bugNode[severity]);
      if (sastType === SASTType.Vulnerability || sastType === SASTType.All)
        resultsArray.push(vulnNode[severity]);
    }
  });
  return resultsArray;
}

function getVulnMetricsByTimeData(
  sourceData: any,
  sastType: SASTType,
  metricsType: MetricsType,
  severityFilter: any,
) {
  let bugData: number[] = [];
  let vulnData: number[] = [];

  severityFilter.forEach((severity) => {
    const bugNode = sourceData[SASTType.Bug][metricsType][severity];
    const vulnNode = sourceData[SASTType.Vulnerability][metricsType][severity];
    const bugValues: any[] = Object.values(bugNode);
    const vulnValues: any[] = Object.values(vulnNode);

    for (var i = 0; i < bugValues.length; i++) {
      if (bugData[i]) bugData[i] += bugValues[i];
      else bugData[i] = bugValues[i];

      if (vulnData[i]) vulnData[i] += vulnValues[i];
      else vulnData[i] = vulnValues[i];
    }
  });

  const resultsArray: number[][] = [];
  if (
    (sastType === SASTType.Bug || sastType === SASTType.All) &&
    checkNonZeroArray(bugData)
  )
    resultsArray.push(bugData);
  if (
    (sastType === SASTType.Vulnerability || sastType === SASTType.All) &&
    checkNonZeroArray(vulnData)
  )
    resultsArray.push(vulnData);
  return resultsArray;
}

function getVulnMetricsTooltips(
  sourceData: any,
  sastType: SASTType,
  metricsType: MetricsType,
  propertyName: string,
) {
  const bugNode = sourceData[SASTType.Bug][metricsType];
  const vulnNode = sourceData[SASTType.Vulnerability][metricsType];

  const bugValues: any[] = Object.values(bugNode);
  const vulnValues: any[] = Object.values(vulnNode);

  const bugData = bugValues.map((x) => x[propertyName]);
  const vulnData = vulnValues.map((x) => x[propertyName]);

  const toolTipLabels: string[] = [];
  if (
    (sastType === SASTType.Bug || sastType === SASTType.All) &&
    checkNonZeroArray(bugData)
  ) {
    toolTipLabels.push(SASTType.Bug);
  }
  if (
    (sastType === SASTType.Vulnerability || sastType === SASTType.All) &&
    checkNonZeroArray(vulnData)
  ) {
    toolTipLabels.push(SASTType.Vulnerability);
  }
  return toolTipLabels;
}
